import * as React from "react";
import { ReactNode } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorIcon from "@mui/icons-material/ErrorRounded";
import InfoIcon from "@mui/icons-material/InfoRounded";
import WarningIcon from "@mui/icons-material/WarningRounded";
import MuiAlert, { alertClasses } from "@mui/material/Alert";
import MuiAlertTitle, { alertTitleClasses } from "@mui/material/AlertTitle";

import styled from "veneer/styled";
import { Colors, FontSizes } from "veneer/Theme";

export interface AlertProps {
  children: ReactNode;
  dataTestId?: string;
  dense?: boolean;
  icon?: ReactNode;
  onClose?: () => void;
  severity?: "error" | "info" | "warning" | "success";
  title?: ReactNode;
}

const iconMapping = {
  error: <ErrorIcon fontSize="inherit" />,
  info: <InfoIcon fontSize="inherit" />,
  warning: <WarningIcon fontSize="inherit" />,
  success: <CheckCircleIcon fontSize="inherit" />,
};

const stylesBySeverity = {
  error: { backgroundColor: Colors.red100, color: Colors.red1000 },
  info: { backgroundColor: Colors.blue100, color: Colors.blue1000 },
  warning: { backgroundColor: Colors.yellow200, color: Colors.yellow1000 },
  success: { backgroundColor: Colors.green200, color: Colors.green1000 },
};

const Root = styled(MuiAlert, {
  shouldForwardProp: (prop) => prop !== "dense",
})<{ dense: boolean }>(({ theme, dense, severity = "info" }) =>
  dense
    ? {
        ...stylesBySeverity[severity],
        padding: theme.spacing(0.5, 2),
        [`.${alertTitleClasses.root}`]: {
          ...theme.typography.h6,
          margin: theme.spacing(-0.25, 0, 0, 0),
          color: "inherit",
        },
        [`.${alertClasses.icon}`]: {
          padding: 0,
          fontSize: "20px",
          marginRight: theme.spacing(0.5),
          color: "inherit",
        },
        [`.${alertClasses.message}`]: {
          fontSize: FontSizes.small,
          padding: theme.spacing(0.25, 0, 0, 0),
        },
        [`.${alertClasses.action}`]: {
          paddingTop: 0,
          "& > .MuiButtonBase-root[title=Close]": {
            maxWidth: "20px",
            maxHeight: "20px",
          },
        },
      }
    : {
        ...stylesBySeverity[severity],
        [`.${alertTitleClasses.root}`]: {
          ...theme.typography.h5,
          color: "inherit",
        },
        [`.${alertClasses.icon}`]: {
          fontSize: "24px",
          color: "inherit",
        },
      },
);

const Alert = ({
  children,
  dataTestId,
  dense = false,
  icon,
  onClose,
  severity = "info",
  title,
  ...rest
}: AlertProps) => {
  return (
    <Root
      {...rest}
      data-component="veneer-alert"
      data-testid={dataTestId}
      dense={dense}
      icon={icon}
      iconMapping={dense ? iconMapping : undefined}
      onClose={onClose}
      severity={severity}
    >
      {!!title && (
        <MuiAlertTitle data-testid="veneer-alert-title">{title}</MuiAlertTitle>
      )}
      {children}
    </Root>
  );
};

export default Alert;
